/**
 * One live transcription session, relayed by the API to the speech provider
 * (Gemini Live over WebSocket) in the provider's own messages. The API sets
 * the session up; audio goes up as 16 kHz PCM frames; captions come back as
 * interim guesses, replaced by a final line each time the speaker pauses.
 * Audio may be sent before the session connects: it is kept, briefly.
 */

export interface LiveTranscriptionHandlers {
  /** The session accepted its setup and is taking audio. */
  onReady: () => void;
  /** A guess at the words being spoken, replaced as it firms up. */
  onInterim: (text: string) => void;
  /** The settled words for the latest stretch of speech. */
  onFinal: (text: string) => void;
  onError: (message: string) => void;
  onClose: (code: number, reason: string) => void;
  /** Audio started being dropped while the session was still connecting. */
  onAudioDropped: () => void;
}

/** Where the session's socket is, and the subprotocols it is opened with. */
export interface LiveTranscriptionSocket {
  url: string;
  protocols: string[];
}

export interface LiveTranscriptionSession {
  /** Opens the socket; audio sent before this waits for it to be ready. */
  connect: (socket: LiveTranscriptionSocket) => void;
  sendAudio: (frame: ArrayBuffer) => void;
  /** Stops sending, waits briefly for the last words, then closes. */
  finish: () => void;
}

const AUDIO_MIME_TYPE = 'audio/pcm;rate=16000';
/**
 * Frames kept while the session connects; 100 ms each, so 15 s, as long as
 * the API waits for the provider to open the session.
 */
const MAX_PENDING_FRAMES = 150;
/** How long to wait for the last words after the audio ends. */
const FINISH_GRACE_MS = 2000;
const CLOSE_NORMAL = 1000;
const CLOSE_NO_STATUS = 1005;

interface ServerMessage {
  setupComplete?: unknown;
  serverContent?: {
    interimInputTranscription?: { text?: string };
    inputTranscription?: { text?: string };
  };
}

// A frame is 100 ms of audio, well within what one call can spread.
const toBase64 = (frame: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(frame)));

const audioMessage = (frame: ArrayBuffer) =>
  JSON.stringify({
    realtimeInput: {
      audio: { data: toBase64(frame), mimeType: AUDIO_MIME_TYPE },
    },
  });

const audioEndMessage = JSON.stringify({
  realtimeInput: { audioStreamEnd: true },
});

// Frames arrive as text or, from this provider, as binary holding JSON.
const readMessage = (data: unknown): ServerMessage | null => {
  try {
    const raw =
      typeof data === 'string'
        ? data
        : new TextDecoder().decode(data as ArrayBuffer);
    return JSON.parse(raw) as ServerMessage;
  } catch {
    // A frame that cannot be read carries no words; dropping it loses nothing.
    return null;
  }
};

const isAbnormalClose = (event: CloseEvent) =>
  event.code !== CLOSE_NORMAL && event.code !== CLOSE_NO_STATUS;

export const createLiveTranscription = (
  handlers: LiveTranscriptionHandlers,
): LiveTranscriptionSession => {
  const pending: string[] = [];
  let socket: WebSocket | null = null;
  let isReady = false;
  let isFinishing = false;
  let isClosedByUs = false;
  let isDropping = false;

  const send = (message: string) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(message);
  };

  const close = () => {
    if (!socket || socket.readyState >= WebSocket.CLOSING) return;
    isClosedByUs = true;
    socket.close(CLOSE_NORMAL);
  };

  const handleReady = () => {
    isReady = true;
    handlers.onReady();
    pending.splice(0).forEach(send);
    if (isFinishing) send(audioEndMessage);
  };

  const handleMessage = (data: unknown) => {
    const message = readMessage(data);
    if (!message) return;
    if (message.setupComplete !== undefined) handleReady();
    const interim = message.serverContent?.interimInputTranscription?.text;
    if (interim) handlers.onInterim(interim);
    const final = message.serverContent?.inputTranscription?.text;
    if (final) handlers.onFinal(final);
  };

  // A close we did not ask for is an error before setup, whatever its code;
  // after setup only an abnormal code is. Every socket error ends in a close,
  // so this is the one place failures are reported: a refused handshake is
  // reported once. Once the recording has stopped before captions began,
  // nobody is waiting on them, so its close is not reported either.
  const handleClose = (event: CloseEvent) => {
    const detail = event.reason
      ? `${event.code} ${event.reason}`
      : `${event.code}`;
    const isExpected = isClosedByUs || (isFinishing && !isReady);
    if (!isExpected && !isReady) {
      handlers.onError(`Live captions closed before starting (${detail}).`);
    } else if (!isExpected && isAbnormalClose(event)) {
      handlers.onError(`Live captions closed (${detail}).`);
    }
    handlers.onClose(event.code, event.reason);
  };

  const connect: LiveTranscriptionSession['connect'] = ({ url, protocols }) => {
    socket = new WebSocket(url, protocols);
    socket.binaryType = 'arraybuffer';
    socket.onmessage = (event) => handleMessage(event.data);
    socket.onclose = handleClose;
  };

  const sendAudio = (frame: ArrayBuffer) => {
    const message = audioMessage(frame);
    if (isReady) {
      send(message);
      return;
    }
    if (pending.length >= MAX_PENDING_FRAMES) {
      pending.shift();
      if (!isDropping) handlers.onAudioDropped();
      isDropping = true;
    }
    pending.push(message);
  };

  const finish = () => {
    isFinishing = true;
    if (isReady) send(audioEndMessage);
    setTimeout(close, FINISH_GRACE_MS);
  };

  return { connect, sendAudio, finish };
};
